import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormField, Input } from '../../../components/ui/FormField.js';
import { type UserRef, discountsApi } from '../../../services/discounts.js';

function customerLabel(u: UserRef) {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.email ?? u._id})` : (u.email ?? u._id);
}

/** Debounced customer search against the admin users list (customer scope). */
export function CustomerPicker({
  value,
  label,
  onSelect,
}: {
  value: string;
  label: string;
  onSelect: (id: string, label: string) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 400);
    return () => clearTimeout(t);
  }, [term]);

  const { data } = useQuery({
    queryKey: ['discount-customer-search', debounced],
    queryFn: () => discountsApi.searchCustomers(debounced),
    enabled: debounced.length >= 2,
  });

  const results = data?.items ?? [];

  return (
    <FormField
      label="Customer"
      name="customer_search"
      required
      hint={
        label
          ? `Selected: ${label}`
          : 'Search by email (min 2 chars), then pick a customer from the list.'
      }
    >
      <Input
        id="customer_search"
        placeholder="Search customers by email…"
        value={term}
        onInput={(e) => setTerm((e.target as HTMLInputElement).value)}
      />
      {results.length > 0 && (
        <ul className="mt-1 max-h-48 divide-y divide-hairline overflow-y-auto rounded-md border border-hairline bg-canvas text-sm">
          {results.map((u) => (
            <li key={u._id}>
              <button
                type="button"
                className={[
                  'block w-full px-3 py-2 text-left hover:bg-surface',
                  u._id === value ? 'text-primary font-medium' : 'text-ink',
                ].join(' ')}
                onClick={() => {
                  onSelect(u._id, customerLabel(u));
                  setTerm('');
                  setDebounced('');
                }}
              >
                {customerLabel(u)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </FormField>
  );
}
